/**
 * Tour times are stored as the property's local wall-clock time (naive), so
 * they are read here as local time with any zone marker stripped.
 */
export const parseTourDate = (dateStr) => {
  if (!dateStr) return null;
  const normalized = String(dateStr).replace(/Z$/, '').replace(/[+-]\d{2}:\d{2}$/, '');
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * The current wall-clock time in `timeZone` (an IANA name such as the
 * listing's `house.timezone`), as a Date whose local fields read that time,
 * so it compares directly with a parsed tour time. Falls back to the
 * viewer's clock when the zone is missing or unknown.
 */
export const propertyNow = (timeZone, now = new Date()) => {
  if (!timeZone) return now;
  try {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
        .formatToParts(now)
        .map(({ type, value }) => [type, value])
    );
    return new Date(
      Number(parts.year), Number(parts.month) - 1, Number(parts.day),
      Number(parts.hour), Number(parts.minute), Number(parts.second)
    );
  } catch (_) {
    return now;
  }
};

/** True when a stored (naive, property-local) time is at or before now at the property. */
export const isPastAtProperty = (dateStr, timeZone, now = new Date()) => {
  const d = parseTourDate(dateStr);
  return !!d && d <= propertyNow(timeZone, now);
};

/** Shown on Mark Complete before the tour has started (QA F13). */
export const TOUR_NOT_STARTED_TEXT = 'You can mark this tour complete once its scheduled time has started.';

/**
 * True once a scheduled tour's start time has arrived, so it can be marked
 * complete (QA F13: a tour three days out could be completed and rated).
 * Compared in the property's time zone (`tour.house.timezone`), as the
 * server does, so a viewer in another zone sees the same answer.
 */
export const hasTourStarted = (tour, now = new Date()) => {
  if (!tour || tour.status !== 'scheduled') return false;
  return isPastAtProperty(tour.scheduled_date, tour.house?.timezone, now);
};

/**
 * How each stored tour status reads on a badge. Same words as
 * TOUR_STATUS_LABELS on the server, which its error messages use.
 */
const TOUR_STATUS_LABELS = {
  pending: 'Pending',
  seller_approved: 'Approved',
  seller_declined: 'Declined',
  scheduled: 'Scheduled',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

/** Human-readable tour status, e.g. `seller_declined` -> "Declined". */
export const tourStatusLabel = (status) =>
  TOUR_STATUS_LABELS[status] || String(status || '').replace(/_/g, ' ');
